import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Screen } from "../components/Screen";
import { Box } from "@mui/material";
import { OfferDropdown } from "components/OfferDropdown";
import DateRangePicker from "../components/DateRangePicker";
import {
	addSelectedOffer,
	fetchPerformance,
	removeSelectedOffer,
	resetPerformanceData,
	resetSelectedOffers,
} from "store/performanceSlice";
import { PerformanceBudget } from "components/PerformanceOffer/PerformanceBudget";
import { PerformanceValue } from "components/PerformanceOffer/PerformanceValue";
import { PerformanceChart } from "components/PerformanceOffer/PerformanceChart";
import { chartColors, colors } from "styles/colors";
import { fetchOffers } from "store/offersSlice";
import { defaultRanges } from "components/DateRangePicker/defaultRanges";
import dayjs from "dayjs";

export const Performance = () => {
	const dispatch = useDispatch();
	const selectedOffers = useSelector(
		(state) => state.performance.selectedOffers,
	);
	const performanceData = useSelector((state) => state.performance.performance);
	const loadingPerformance = useSelector(
		(state) => state.performance.loadingPerformance,
	);
	const offers = useSelector((state) => state.offers.offers);
	const [dateRange, setDateRange] = useState({
		startDate: defaultRanges.startOfAllTime,
		endDate: defaultRanges.endOfToday,
	});

	const offerNames = useMemo(
		() => selectedOffers.map((selectedOffer) => selectedOffer.name),
		[selectedOffers],
	);

	const selectedBrand = useSelector(({ brands }) => brands.selectedBrand);
	const loadingOffers = useSelector(({ offers }) => offers.loadingOffers);

	useEffect(() => {
		if (selectedBrand) {
			dispatch(resetSelectedOffers());
			dispatch(resetPerformanceData());

			dispatch(fetchOffers());
		}
	}, [dispatch, selectedBrand]);

	useEffect(() => {
		if (selectedOffers.length) {
			dispatch(
				fetchPerformance({
					startDate: dayjs(dateRange.startDate).format("YYYY-MM-DD"),
					endDate: dayjs(dateRange.endDate).format("YYYY-MM-DD"),
					offerId: selectedOffers.map((offer) => offer.id),
				}),
			);
		} else if (performanceData) {
			dispatch(resetPerformanceData());
		}
	}, [dispatch, selectedOffers, dateRange]);

	useEffect(() => {
		return () => {
			dispatch(resetSelectedOffers());
			dispatch(resetPerformanceData());
		};
	}, []);

	return (
		<Screen title={"Performance"}>
			<Box sx={{ display: "flex", mb: "16px" }}>
				<OfferDropdown
					selectedOffers={selectedOffers}
					offers={offers}
					onAddOffer={(offer) => dispatch(addSelectedOffer(offer))}
					onRemoveOffer={(offer) => dispatch(removeSelectedOffer(offer))}
					containerStyle={{ mr: "8px" }}
					loading={!selectedBrand || loadingOffers}
				/>
				<DateRangePicker value={dateRange} onChange={setDateRange} />
			</Box>
			{Object.keys(performanceData).length ? (
				<>
					<Box sx={{ display: "flex", mb: "16px" }}>
						<PerformanceValue
							name="Budget Spent"
							colorTitle={chartColors.darkGreen}
							value={performanceData.budget[0].spent}
							containerStyle={{ mr: "20px" }}
							units={"SAR"}
							loading={loadingPerformance}
						/>
						<PerformanceValue
							name="Budget Spent"
							colorTitle={chartColors.purple}
							value={performanceData.budget?.[1]?.spent}
							units={"SAR"}
							loading={loadingPerformance}
						/>
					</Box>
					<PerformanceBudget
						data={performanceData.budget}
						offerNames={offerNames}
						containerStyle={{ mb: "16px" }}
						// somehow nivo bar chart lib does not show right order in data array, so we need to use this condition
						remainingTextColors={
							performanceData.budget.length === 1
								? [colors.primary]
								: [colors.primary, "#6A60C5"]
						}
						spentTextColors={
							performanceData.budget.length === 1
								? [colors.primary]
								: [colors.primary, chartColors.purple]
						}
						offerColors={[chartColors.darkGreen, chartColors.purple]}
						loading={loadingPerformance}
					/>
					<Box sx={{ display: "flex", mb: "16px" }}>
						<PerformanceValue
							name="No. of transactions"
							colorTitle={chartColors.darkGreen}
							value={performanceData.noTransactions.values[0]}
							containerStyle={{ mr: "20px" }}
							loading={loadingPerformance}
						/>
						<PerformanceValue
							name="No. of transactions"
							colorTitle={chartColors.purple}
							value={performanceData.noTransactions.values?.[1]}
							loading={loadingPerformance}
						/>
					</Box>
					<PerformanceChart
						name={"No. of transactions"}
						offerNames={offerNames}
						colors={[chartColors.darkGreen, chartColors.purple]}
						data={performanceData.noTransactions.chart}
						containerStyle={{ mb: "16px" }}
						axisLeftTickValues={
							performanceData.noTransactions.axisLeftTickValues
						}
						loading={loadingPerformance}
					/>
					<Box sx={{ display: "flex", mb: "16px" }}>
						<PerformanceValue
							name="Sales"
							colorTitle={chartColors.darkGreen}
							value={performanceData.sales.values[0]}
							containerStyle={{ mr: "20px" }}
							units={"SAR"}
							loading={loadingPerformance}
						/>
						<PerformanceValue
							name="Sales"
							colorTitle={chartColors.purple}
							value={performanceData.sales.values?.[1]}
							units={"SAR"}
							loading={loadingPerformance}
						/>
					</Box>
					<PerformanceChart
						name={"Sales"}
						colors={[chartColors.darkGreen, chartColors.purple]}
						offerNames={offerNames}
						data={performanceData.sales.chart}
						containerStyle={{ mb: "16px" }}
						axisLeftTickValues={performanceData.sales.axisLeftTickValues}
						loading={loadingPerformance}
					/>
					<Box sx={{ display: "flex", mb: "16px" }}>
						<PerformanceValue
							name="AOV"
							colorTitle={chartColors.darkGreen}
							value={performanceData.aov.values[0]}
							containerStyle={{ mr: "20px" }}
							notApplicable
							units={"SAR"}
							loading={loadingPerformance}
						/>
						<PerformanceValue
							name="AOV"
							colorTitle={chartColors.purple}
							value={performanceData.aov.values?.[1]}
							notApplicable
							units={"SAR"}
							loading={loadingPerformance}
						/>
					</Box>
					<PerformanceChart
						name={"AOV"}
						colors={[chartColors.darkGreen, chartColors.purple]}
						offerNames={offerNames}
						data={performanceData.aov.chart}
						containerStyle={{ mb: "16px" }}
						axisLeftTickValues={performanceData.aov.axisLeftTickValues}
						loading={loadingPerformance}
					/>
					<Box sx={{ display: "flex", mb: "16px" }}>
						<PerformanceValue
							name="ROAS"
							colorTitle={chartColors.darkGreen}
							value={performanceData.roas.values[0]}
							containerStyle={{ mr: "20px" }}
							notApplicable
							units={"%"}
							loading={loadingPerformance}
						/>
						<PerformanceValue
							name="ROAS"
							colorTitle={chartColors.purple}
							value={performanceData.roas.values?.[1]}
							notApplicable
							units={"%"}
							loading={loadingPerformance}
						/>
					</Box>
					<PerformanceChart
						name={"ROAS"}
						colors={[chartColors.darkGreen, chartColors.purple]}
						offerNames={offerNames}
						data={performanceData.roas.chart}
						axisLeftTickValues={performanceData.roas.axisLeftTickValues}
						loading={loadingPerformance}
					/>
				</>
			) : null}
		</Screen>
	);
};
